import React, { useId } from "react";
import { cn } from "../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../internal/primitives/select";
import type { MapLayerSelectorProps } from "../../contracts/map-workspace";

export function MapLayerSelector({
  value,
  options,
  label,
  helperText,
  placeholder = "Select layer...",
  disabled,
  onValueChange,
  className,
}: MapLayerSelectorProps) {
  const triggerId = useId();
  const helperTextId = useId();
  const selectedOption = options.find((opt) => opt.id === value);

  return (
    <div className={cn("flex flex-col gap-1.5 w-full", className)}>
      {label && (
        <label
          htmlFor={triggerId}
          className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80"
        >
          {label}
        </label>
      )}
      
      <Select
        value={value ?? undefined}
        onValueChange={onValueChange}
        disabled={disabled || options.length === 0}
      >
        <SelectTrigger
          id={triggerId}
          aria-label={label ?? placeholder}
          aria-describedby={helperText ? helperTextId : undefined}
          className="h-8 px-2 py-1 bg-muted/30 border-border/50 hover:bg-muted/50 transition-colors shadow-none focus:ring-0 focus:border-primary/50"
        >
          <SelectValue placeholder={placeholder}>
            {selectedOption && (
              <div className="flex items-center gap-2 overflow-hidden">
                <span className="text-xs font-bold truncate">
                  {selectedOption.label}
                </span>
                {selectedOption.metaLabel && (
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-background border border-border/50 text-muted-foreground shrink-0 uppercase tracking-tighter">
                    {selectedOption.metaLabel}
                  </span>
                )}
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.length === 0 ? (
            <div className="px-2 py-4 text-center">
              <p className="text-[10px] text-muted-foreground italic font-medium">
                No rendered layers available
              </p>
            </div>
          ) : (
            options.map((option) => (
              <SelectItem
                key={option.id}
                value={option.id}
                disabled={option.disabled}
                className="py-2"
              >
                <div className="flex flex-col gap-0.5 min-w-0 pr-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold truncate">
                      {option.label}
                    </span>
                    {option.metaLabel && (
                      <span className="text-[9px] font-mono px-1 rounded-sm bg-muted text-muted-foreground shrink-0 uppercase tracking-tighter">
                        {option.metaLabel}
                      </span>
                    )}
                  </div>
                  {option.description && (
                    <p className="text-[10px] text-muted-foreground leading-tight line-clamp-2 italic">
                      {option.description}
                    </p>
                  )}
                </div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {helperText && (
        <p id={helperTextId} className="text-[10px] text-muted-foreground leading-snug italic font-medium mt-0.5">
          {helperText}
        </p>
      )}
    </div>
  );
}
